import { MediaCarousel } from '@euler/app/components/media/MediaCarousel';
import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { DeliveryCheckCellModel } from '@euler/app/flows/report/functions/useDeliveryCheckReportModels';
import { Center } from '@euler/components/Center';
import { Collapsible } from '@euler/components/collapsible';
import { Colors } from '@euler/components/Colors';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { MediaObject } from '@euler/model';
import { formatDateTime } from '@euler/utils/formatters';
import { memo, useMemo } from 'react';
import { StyleProp, StyleSheet, Text, View, ViewStyle } from 'react-native';
import { useTheme } from 'styled-components';

export const DeliveryCheckCell = memo(
  ({
    model,
    width,
    hideInfo,
    style,
    infoStyle,
    mediaListContainerStyle,
    remarkContainerStyle,
  }: {
    model: DeliveryCheckCellModel;
    width: number;
    hideInfo?: boolean;
    style?: StyleProp<ViewStyle>;
    infoStyle?: StyleProp<ViewStyle>;
    mediaListContainerStyle?: StyleProp<ViewStyle>;
    remarkContainerStyle?: StyleProp<ViewStyle>;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const header = useMemo(() => <Header model={model} />, [model]);

    return (
      <Collapsible.Panel
        header={header}
        caretColor={theme.report.construction.section.header.titleColor}
        headerStyle={{ alignItems: 'flex-start' }}
        css={`
          margin-top: ${rpx(10)}px;
          background-color: ${theme.section.background};
        `}
        style={style}
      >
        <Content
          model={model}
          width={width}
          hideInfo={hideInfo}
          infoStyle={infoStyle}
          mediaListContainerStyle={mediaListContainerStyle}
          remarkContainerStyle={remarkContainerStyle}
        />
      </Collapsible.Panel>
    );
  },
);

const Header = memo(({ model }: { model: DeliveryCheckCellModel }) => {
  const theme = useTheme();
  const { rpx } = useResponsiveContext();
  return (
    <>
      <Text
        css={`
          font-weight: bold;
          font-style: italic;
          font-size: ${rpx(28)}px;
          line-height: ${rpx(30)}px;
          color: ${theme.report.construction.section.header.titleColor};
        `}
      >
        {model.index + 1}.{' '}
      </Text>
      <Text
        css={`
          font-family: ${FontFamily.NotoSans.Medium};
          font-size: ${rpx(21)}px;
          line-height: ${rpx(30)}px;
          color: ${theme.report.construction.section.header.titleColor};
          flex: 1;
        `}
      >
        {model.title}
      </Text>
    </>
  );
});

const kSectionContentSpacing = 15;

const Content = memo(
  ({
    model,
    width,
    hideInfo,
    infoStyle,
    mediaListContainerStyle,
    remarkContainerStyle,
  }: {
    model: DeliveryCheckCellModel;
    width: number;
    hideInfo?: boolean;
    infoStyle?: StyleProp<ViewStyle>;
    mediaListContainerStyle?: StyleProp<ViewStyle>;
    remarkContainerStyle?: StyleProp<ViewStyle>;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const contentWidth = width - rpx(kSectionContentSpacing) * 2;
    const mediaHeight = contentWidth * 0.75;
    return (
      <>
        {!hideInfo && (
          <HStack style={infoStyle}>
            <Text
              css={`
                font-family: ${FontFamily.NotoSans.Regular};
                font-size: ${rpx(16)}px;
                color: ${Colors.Gray3};
                line-height: ${rpx(24)}px;
                margin-top: ${rpx(8)}px;
              `}
            >
              质检: {model.technicianName}
            </Text>
            {model.timestamp != null && (
              <Typography.Label
                light
                size={rpx(14)}
                color="#555"
                lineHeight={rpx(24)}
              >
                {formatDateTime(model.timestamp)}
              </Typography.Label>
            )}
          </HStack>
        )}
        <MediaCarousel
          medias={model.medias}
          noPlaceholder
          css={`
            width: ${contentWidth}px;
            height: ${mediaHeight}px;
            border-radius: ${rpx(16)}px;
            overflow: hidden;
            margin-top: ${rpx(10)}px;
          `}
          style={mediaListContainerStyle}
        />
        {model.reworkRecords?.length ? (
          <View
            css={`
              margin-bottom: ${rpx(8)}px;
            `}
          >
            <StackLayout
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mt={rpx(12)}
              gap={rpx(5)}
            >
              <Typography.Label medium color="#555" flex={1}>
                内返记录:
              </Typography.Label>
            </StackLayout>
            <View
              css={`
                height: ${StyleSheet.hairlineWidth}px;
                background-color: #ddd;
                margin: ${rpx(4)}px 0;
              `}
            />
            <StackLayout direction="row" mt={rpx(4)} mb={rpx(8)} gap={rpx(6)}>
              <Typography.Text color="#555">
                责任人: {model.reworkRecords[0].responsiblePersonName}, 备注:{' '}
                {model.reworkRecords[0].remarks?.trim() || '(无)'}
              </Typography.Text>
            </StackLayout>
            <MediaCarousel
              numberOfItemsPerRow={5}
              noPlaceholder
              display="list"
              itemSpacer={rpx(8)}
              playIconSize={rpx(12)}
              itemStyle={{
                borderRadius: rpx(8),
                overflow: 'hidden',
              }}
              medias={model.reworkRecords
                .flatMap(x => x.medias)
                .map<MediaObject>(x => ({
                  id: x.id,
                  url: x.url,
                  type: x.type.includes('video') ? 'video' : 'image',
                  coverUrl: x.coverUrl,
                  title: x.title,
                  subTitle: x.subTitle,
                  description: x.remark,
                }))}
            />
          </View>
        ) : null}
        <View
          css={`
            flex-direction: row;
            align-items: flex-start;
            justify-content: flex-start;
            margin-top: ${rpx(10)}px;
          `}
          style={remarkContainerStyle}
        >
          <Center
            css={`
              background-color: ${model.resultCode === 'OK'
                ? theme.colors.status.success
                : theme.colors.status.danger};
              margin-right: ${rpx(4)}px;
              padding: 0 ${rpx(4)}px;
              padding: 0 ${rpx(5)}px;
              border-radius: ${rpx(5)}px;
              height: ${rpx(20)}px;
            `}
          >
            <Text
              css={`
                font-family: ${FontFamily.NotoSans.Light};
                font-size: ${rpx(12)}px;
                text-align: center;
                color: #fff;
                line-height: ${rpx(16)}px;
              `}
            >
              备注
            </Text>
          </Center>
          <Text
            css={`
              flex: 1;
              font-family: ${FontFamily.NotoSans.Regular};
              font-size: ${rpx(16)}px;
              margin-right: ${rpx(4)}px;
              line-height: ${rpx(20)}px;
              color: ${model.resultCode === 'OK'
                ? Colors.Gray2
                : theme.colors.status.danger};
            `}
          >
            {model.remark}
            {model.resultCode !== 'OK'
              ? `: ${model.unacceptedReason || '未知原因'}`
              : ''}
          </Text>
        </View>
      </>
    );
  },
);
